'use client';

import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Tooltip, ClickAwayListener } from '@mui/material';
import styled from '@emotion/styled';
import { borderRadius, breakpoints, color, spacing } from '@/theme/tokens';
import ButtonContainedBrand from '@/components/formElements/buttons/ButtonContainedBrand';
import Checkbox from '@/components/formElements/Checkbox';
import axios from '@/config/axios';
import { broadcastContractAccepted, subscribeContractAccepted } from '@/utils/contractAcceptanceBroadcast';
import type { ContractType } from '@/components/hooks/useContractsData';

type ContractAgreementProps = {
    text: string;
    checkboxTooltip: string;
    buttonTooltip: string;
    buttonText: string;
    contractType: ContractType;
    onAcceptComplete: () => void;
    isScrolledToBottom?: boolean;
    style?: React.CSSProperties;
};

const Wrapper = styled(Box)`
    padding: ${spacing[32]};
    background-color: ${color.white[0]};
    border-radius: ${borderRadius[8]};
    display: flex;
    flex-direction: column;
    gap: ${spacing[32]};
    margin-top: ${spacing[32]};

    @media (max-width: ${breakpoints.md}) {
        padding: ${spacing[24]};
        gap: ${spacing[24]};
        margin-top: ${spacing[24]};
    }

    @media (max-width: ${breakpoints.sm}) {
        padding: ${spacing[16]};
        gap: ${spacing[16]};
        margin-top: ${spacing[16]};
    }
`;

const CheckboxWrapper = styled(Box)`
    display: flex;
    align-items: center;
    align-content: center;
`;

const AgreementText = styled(Typography)`
    color: ${color.brand[50]};
`;

const ButtonWrapper = styled(Box)`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ErrorMessage = styled(Typography)`
    color: ${color.brand[50]};
    text-align: center;
`;

const ClickableWrapper = styled(Box)<{ clickable?: boolean }>`
    display: inline-flex;
    position: relative;

    ${props =>
        props.clickable &&
        `
        &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            cursor: pointer;
            z-index: 1;
        }
    `}
`;

const ContractAgreement = ({
    text,
    checkboxTooltip,
    buttonTooltip,
    contractType,
    buttonText,
    onAcceptComplete,
    isScrolledToBottom = true,
    style,
}: ContractAgreementProps) => {
    const [isChecked, setIsChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [checkboxTooltipOpen, setCheckboxTooltipOpen] = useState(false);
    const [buttonTooltipOpen, setButtonTooltipOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const onAcceptCompleteRef = useRef(onAcceptComplete);
    useEffect(() => {
        onAcceptCompleteRef.current = onAcceptComplete;
    }, [onAcceptComplete]);

    // When the same contract is accepted in another tab, mirror the local success flow
    // so this tab hides the form and refetches data instead of allowing a duplicate submit.
    useEffect(() => {
        return subscribeContractAccepted(acceptedContractType => {
            if (acceptedContractType === contractType) {
                onAcceptCompleteRef.current();
            }
        });
    }, [contractType]);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsChecked(event.target.checked);
        setError(null);
        if (event.target.checked) {
            setCheckboxTooltipOpen(false);
            setButtonTooltipOpen(false);
        }
    };

    const handleCheckboxTooltipOpen = () => {
        if (!isChecked) {
            setCheckboxTooltipOpen(true);
        }
    };

    const handleCheckboxTooltipClose = () => {
        setCheckboxTooltipOpen(false);
    };

    const handleButtonTooltipOpen = () => {
        if (!isChecked) {
            setButtonTooltipOpen(true);
        }
    };

    const handleButtonTooltipClose = () => {
        setButtonTooltipOpen(false);
    };

    // React state updates are async, so the `loading` prop can't disable the button
    // within the same tick — only a synchronous ref reliably blocks re-entry on
    // double-fire (rapid double click, programmatic .click(), key auto-repeat).
    const isSubmittingRef = useRef(false);

    const handleAccept = async () => {
        if (!isChecked || isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        setIsLoading(true);
        setError(null);

        try {
            await axios.post(`/api/contracts/${contractType}/accept`);
            broadcastContractAccepted(contractType);
            onAcceptComplete();
        } catch (err) {
            if (axios.isAxiosError(err) && err.response) {
                setError(err.response.data?.message || 'Failed to accept contract');
            } else {
                setError('Network error occurred');
            }
        } finally {
            isSubmittingRef.current = false;
            setIsLoading(false);
        }
    };

    const checkboxTooltipContent = (
        <Tooltip
            title={checkboxTooltip}
            arrow
            placement="top"
            open={isChecked ? false : checkboxTooltipOpen}
            onClose={handleCheckboxTooltipClose}
            {...(isMobile
                ? {
                      disableFocusListener: true,
                      disableHoverListener: true,
                      disableTouchListener: true,
                  }
                : {
                      onOpen: handleCheckboxTooltipOpen,
                  })}
        >
            <ClickableWrapper
                clickable={isMobile && !isChecked}
                onClick={isMobile && !isChecked ? handleCheckboxTooltipOpen : undefined}
                onTouchEnd={
                    isMobile && !isChecked
                        ? e => {
                              e.preventDefault();
                              handleCheckboxTooltipOpen();
                          }
                        : undefined
                }
            >
                <Checkbox label="" checked={false} onChange={() => {}} disabled={true} />
            </ClickableWrapper>
        </Tooltip>
    );

    const buttonTooltipContent = (
        <Tooltip
            title={buttonTooltip}
            arrow
            placement="top"
            open={!isChecked ? buttonTooltipOpen : false}
            onClose={handleButtonTooltipClose}
            {...(isMobile
                ? {
                      disableFocusListener: true,
                      disableHoverListener: true,
                      disableTouchListener: true,
                  }
                : {
                      onOpen: handleButtonTooltipOpen,
                  })}
        >
            <ClickableWrapper
                clickable={isMobile && !isChecked}
                onClick={isMobile && !isChecked ? handleButtonTooltipOpen : undefined}
                onTouchEnd={
                    isMobile && !isChecked
                        ? e => {
                              e.preventDefault();
                              handleButtonTooltipOpen();
                          }
                        : undefined
                }
            >
                <ButtonContainedBrand onClick={handleAccept} disabled={!isChecked} loading={isLoading}>
                    <Typography variant="label5medium">{buttonText}</Typography>
                </ButtonContainedBrand>
            </ClickableWrapper>
        </Tooltip>
    );

    return (
        <Wrapper style={style}>
            <CheckboxWrapper>
                {!isScrolledToBottom ? (
                    isMobile ? (
                        <ClickAwayListener onClickAway={handleCheckboxTooltipClose}>
                            <div>{checkboxTooltipContent}</div>
                        </ClickAwayListener>
                    ) : (
                        checkboxTooltipContent
                    )
                ) : (
                    <Checkbox label="" checked={isChecked} onChange={handleCheckboxChange} />
                )}

                <AgreementText variant="label5semibold">{text}</AgreementText>
            </CheckboxWrapper>
            {error && <ErrorMessage variant="label5regular">{error}</ErrorMessage>}
            <ButtonWrapper>
                {isMobile ? (
                    <ClickAwayListener onClickAway={handleButtonTooltipClose}>
                        <div>{buttonTooltipContent}</div>
                    </ClickAwayListener>
                ) : (
                    buttonTooltipContent
                )}
            </ButtonWrapper>
        </Wrapper>
    );
};

export default ContractAgreement;
